import React, { useEffect, useState } from "react";
import {
  completeMigrationWithPassword,
  exchangeMigrationLink,
} from "../../api/accountMigration";
import { startVkAuth, isVkConfigured } from "../../platform/social/vk";
import PhoneVerificationField, {
  PhoneVerificationValue,
} from "./PhoneVerificationField";
import "./AccountMigrationPage.css";

type Phase = "loading" | "ready" | "submitting" | "error";

const AccountMigrationPage: React.FC = () => {
  const [phase, setPhase] = useState<Phase>("loading");
  const [migrationToken, setMigrationToken] = useState("");
  const [phoneVerification, setPhoneVerification] =
    useState<PhoneVerificationValue>({phone: "+7", verificationToken: null});
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const hash = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash;
    const token =
      new URLSearchParams(hash).get("token") ||
      new URLSearchParams(window.location.search).get("token") ||
      "";
    window.history.replaceState({}, document.title, "/account-migration");
    if (!token) {
      setError("Ссылка для переноса аккаунта отсутствует или уже недействительна.");
      setPhase("error");
      return;
    }
    exchangeMigrationLink(token)
      .then((response) => {
        setMigrationToken(response.migrationToken);
        setPhase("ready");
      })
      .catch(() => {
        setError("Ссылка недействительна или срок её действия истёк.");
        setPhase("error");
      });
  }, []);

  const phoneIsVerified = !!phoneVerification.verificationToken;

  const finishWithPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    if (!phoneVerification.verificationToken) {
      setError("Сначала подтвердите номер телефона.");
      return;
    }
    if (password.length < 6) {
      setError("Пароль должен содержать не менее 6 символов.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Пароли не совпадают.");
      return;
    }
    try {
      setPhase("submitting");
      await completeMigrationWithPassword({
        migrationToken,
        phone: phoneVerification.phone,
        phoneVerificationToken: phoneVerification.verificationToken,
        password,
      });
      window.location.assign("/web");
    } catch (requestError: any) {
      setError(requestError?.message || "Не удалось завершить перенос аккаунта.");
      setPhase("ready");
    }
  };

  const finishWithVk = async () => {
    if (!phoneVerification.verificationToken) {
      setError("Сначала подтвердите номер телефона.");
      return;
    }
    setError("");
    try {
      await startVkAuth({
        mode: "migration",
        migration: {
          migrationToken,
          phone: phoneVerification.phone,
          phoneVerificationToken: phoneVerification.verificationToken,
        },
      });
    } catch (requestError: any) {
      setError(requestError?.message || "Не удалось открыть вход через VK.");
    }
  };

  return (
    <main className="migration-page">
      <section className="migration-card">
        <div className="migration-logo" aria-hidden="true">🌱</div>
        <h1>Сохраните доступ к аккаунту</h1>
        <p className="migration-intro">
          В связи с требованиями Федерального закона № 199-ФЗ вход через Google
          и Apple больше не поддерживается. Подтвердите российский номер телефона,
          затем установите пароль или привяжите VK. Все данные вашего аккаунта
          сохранятся.
        </p>

        {phase === "loading" && <p role="status">Проверяем защищённую ссылку…</p>}
        {phase === "error" && <div className="migration-error" role="alert">{error}</div>}

        {(phase === "ready" || phase === "submitting") && (
          <>
            <div className="migration-step">
              <span>1</span>
              <div>
                <h2>Подтвердите номер телефона</h2>
                <p>Используйте российский мобильный номер, начинающийся с +7.</p>
              </div>
            </div>
            <PhoneVerificationField
              disabled={phase === "submitting"}
              onChange={setPhoneVerification}
              testIdPrefix="migration"
            />

            {phoneIsVerified && (
              <div className="migration-methods">
                <div className="migration-step">
                  <span>2</span>
                  <div>
                    <h2>Выберите новый способ входа</h2>
                    <p>Можно установить пароль или использовать VK.</p>
                  </div>
                </div>

                <form onSubmit={finishWithPassword} className="migration-password-form">
                  <h3>Вход по email и паролю</h3>
                  <label htmlFor="migration-password">Новый пароль</label>
                  <input
                    id="migration-password"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Новый пароль — минимум 6 символов"
                    autoComplete="new-password"
                    maxLength={128}
                    required
                  />
                  <label htmlFor="migration-password-confirm">Повторите пароль</label>
                  <input
                    id="migration-password-confirm"
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder="Повторите новый пароль"
                    autoComplete="new-password"
                    maxLength={128}
                    required
                  />
                  <button type="submit" disabled={phase === "submitting"}>
                    {phase === "submitting" ? "Сохраняем…" : "Установить пароль и продолжить"}
                  </button>
                </form>

                {isVkConfigured() && (
                  <div className="migration-vk-block">
                    <div className="migration-divider"><span>или</span></div>
                    <button type="button" className="migration-vk-button" onClick={finishWithVk}>
                      Продолжить через VK
                    </button>
                  </div>
                )}
              </div>
            )}
            {error && <div className="migration-error" role="alert">{error}</div>}
          </>
        )}
      </section>
    </main>
  );
};

export default AccountMigrationPage;
